import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    getAuth,
    onAuthStateChanged,
    signInWithPopup,
    signOut,
    GoogleAuthProvider,
    updateProfile,
    deleteUser as firebaseDeleteUser,
    reauthenticateWithCredential,
    EmailAuthProvider,
    fetchSignInMethodsForEmail
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, onSnapshot, collection, addDoc, serverTimestamp, query, orderBy, limit, updateDoc, deleteField } from 'firebase/firestore';
import {
    getUserRole,
    setUserRole,
    deleteUserAccount,
    setUserId,
    getUserById,
    generateUniqueId,
    setUserEmail, // Nově přidaná funkce
} from '../firestore';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState(null);
    const [watchlistState, setWatchlistState] = useState({ map: {}, items: [] });
    const [notifications, setNotifications] = useState([]);
    const [notifSeenAt, setNotifSeenAt] = useState(0); // ms epoch
    const [seenKey, setSeenKey] = useState(null); // localStorage key for per-user persistence
    const [followedMap, setFollowedMap] = useState({}); // { [popupId]: true }

    useEffect(() => {
        // Pre-hydrate from localStorage for instant header (role/VIP) without flicker
        try {
            const cachedRole = localStorage.getItem('auth.role');
            if (cachedRole) setRole(cachedRole);
        } catch {}

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setLoading(true);
            if (user) {
                try {
                    setUser(user);
                        try { window.__CURRENT_USER_ID__ = user.uid; } catch (_) {}
                    // set per-user key and hydrate notifSeenAt early from localStorage to avoid initial overcount
                    const key = `notif.seenAt.${user.uid}`;
                    setSeenKey(key);
                    try {
                        const ls = Number(localStorage.getItem(key) || 0);
                        if (ls && !Number.isNaN(ls)) setNotifSeenAt((prev) => Math.max(prev || 0, ls));
                    } catch {}

                    // Získání role uživatele
                    let userRole = await getUserRole(user.uid);
                    if (!userRole) {
                        await setUserRole(user.uid, 'User');
                        userRole = 'User';
                    }
                    setRole(userRole);
                    try { localStorage.setItem('auth.role', userRole); } catch {}

                    // Získání dokumentu uživatele a generování nového ID, pokud neexistuje
                    let userDoc = await getUserById(user.uid);
                    if (!userDoc.ID) {
                        console.log('Uživatelský dokument neexistuje, generuji nové ID.');
                        const uniqueId = await generateUniqueId();
                        console.log('Generované unikátní ID:', uniqueId);
                        await setUserId(user.uid, uniqueId); // Uložení unikátního ID do Firestore
                    } else {
                        console.log(`Existující ID: ${userDoc.ID}`);
                    }

                    // Nastavení emailu v dokumentu, pokud ještě není nastaven
                    if (!userDoc.email) {
                        console.log('Nastavuji email uživateli.');
                        await setUserEmail(user.uid, user.email);
                    }
                } catch (error) {
                    console.error('Chyba při získávání role uživatele:', error);
                }
            } else {
                    setUser(null);
                    try { delete window.__CURRENT_USER_ID__; } catch (_) {}
                setRole(null);
                setWatchlistState({ map: {}, items: [] });
                setSeenKey(null);
                try { localStorage.removeItem('auth.role'); } catch {}
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Subscribe to user's watchlist once at top-level so it doesn't refetch on every route
    useEffect(() => {
        if (!user?.uid) return;
        const ref = doc(db, 'users', user.uid);
        const unsub = onSnapshot(ref, (snap) => {
            const data = snap.exists() ? (snap.data() || {}) : {};
            const wl = data.watchlist || {};
            const map = wl;
            const items = Object.keys(wl).map((k) => ({ id: k, popupId: k, ...(wl[k] || {}) }));
            setWatchlistState({ map, items });
            // followed
            try { setFollowedMap(data.followed || {}); } catch {}
            // track notification last seen timestamp
            try {
                const ts = data.lastSeenNotificationsAt; // may be Firestore Timestamp
                let ms = 0;
                if (ts && typeof ts.toMillis === 'function') ms = ts.toMillis();
                else if (typeof ts === 'number') ms = ts;
                // merge with localStorage-cached value to avoid regressions
                let ls = 0;
                try { if (seenKey) { const v = Number(localStorage.getItem(seenKey) || 0); if (!Number.isNaN(v)) ls = v; } } catch {}
                setNotifSeenAt((prev) => Math.max(prev || 0, ms || 0, ls || 0));
            } catch {}
        }, (err) => {
            console.warn('watchlist subscribe error', err);
        });
        return () => unsub && unsub();
    }, [user?.uid, seenKey]);

    // Global notifications subscription
    useEffect(() => {
        let alive = true;
        const fetchNow = async () => {
            try {
                const r = await fetch('/api/notifications?limit=100&_=' + Date.now(), { cache: 'no-store' });
                if (!r.ok) return;
                const arr = await r.json();
                if (!alive) return;
                setNotifications(Array.isArray(arr) ? arr : []);
            } catch (e) {
                console.warn('notifications fetch error', e);
            }
        };
        fetchNow();
        const id = setInterval(fetchNow, 15000);
        const onFocus = () => fetchNow();
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => { alive = false; clearInterval(id); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
    }, []);

    // actions
    const markNotificationsSeen = async () => {
        // update local timestamp immediately for instant UI feedback
        const now = Date.now();
        try {
            setNotifSeenAt(now);
            if (seenKey) localStorage.setItem(seenKey, String(now));
        } catch {}
        if (!user?.uid) return;
        try {
            await updateDoc(doc(db, 'users', user.uid), { lastSeenNotificationsAt: serverTimestamp() });
        } catch (e) { console.warn('markNotificationsSeen error', e); }
    };

    const createNotification = async ({ text, popupId, popupTitle, popupThumb, url }) => {
        if (!user || !role || !['Majitel','Admin'].includes(role)) throw new Error('Not allowed');
        try {
            const payload = {
                text: String(text||'').trim(),
                popupId: popupId ? String(popupId) : null,
                popupTitle: popupTitle || null,
                popupThumb: popupThumb || null,
                url: url ? String(url) : null,
                createdBy: user.uid,
            };
            const r = await fetch('/api/notifications', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (!r.ok) throw new Error('create failed');
            // optimistic refresh
            try {
                const list = await r.json();
                setNotifications((prev) => {
                    const ns = Array.isArray(prev) ? prev.slice() : [];
                    if (list && list.id) ns.unshift(list);
                    return ns.slice(0, 100);
                });
            } catch {}
        } catch (e) {
            console.error('createNotification error', e);
            throw e;
        }
    };

    const deleteNotification = async (id) => {
        if (!user || !role || !['Majitel','Admin'].includes(role)) throw new Error('Not allowed');
        const nid = String(id || '');
        if (!nid) throw new Error('id required');
        try {
            const r = await fetch(`/api/notifications/${encodeURIComponent(nid)}`, { method: 'DELETE' });
            if (!r.ok) throw new Error('delete failed');
            // optimistic local removal
            setNotifications((prev) => (Array.isArray(prev) ? prev.filter(n => n?.id !== nid) : []));
        } catch (e) {
            console.error('deleteNotification error', e);
            throw e;
        }
    };

    const toggleFollow = async (popupId, next) => {
        if (!user?.uid) throw new Error('Not logged in');
        const pid = String(popupId || '');
        if (!pid) throw new Error('popupId required');
        try {
            // optimistic
            setFollowedMap(prev => ({ ...prev, [pid]: next ? true : undefined }));
            const ref = doc(db, 'users', user.uid);
            if (next) {
                await updateDoc(ref, { [`followed.${pid}`]: true });
            } else {
                await updateDoc(ref, { [`followed.${pid}`]: deleteField() });
            }
        } catch (e) {
            console.warn('toggleFollow error', e);
            // revert if failed
            setFollowedMap(prev => {
                const copy = { ...prev };
                if (next) delete copy[pid]; else copy[pid] = true;
                return copy;
            });
            throw e;
        }
    };

    const login = async (provider) => {
        if (!auth) {
            alert('Firebase Auth není nakonfigurované (zkontroluj .env REACT_APP_FIREBASE_ENABLED a klíče).');
            return;
        }

        try {
            setLoading(true);
            const res = await signInWithPopup(auth, provider);
            setUser(res.user);
        } catch (err) {
            // detailní logging pro rychlé debugování
            console.error('Firebase login error:', err);
            console.error('code:', err.code, 'message:', err.message, 'customData:', err.customData);
            // fallback -> redirect
            try {
                await signInWithRedirect(auth, provider);
            } catch (err2) {
                console.error('Firebase login redirect error:', err2);
                alert(`Přihlášení selhalo: ${err.code || err.message || 'unknown error'}. Zkontroluj konzoli a konfiguraci Firebase.`);
            }
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
            setUser(null);
            setRole(null);
        } catch (error) {
            console.error('Chyba při odhlášení:', error);
        }
    };

    const updateAvatar = async (avatarURL) => {
        const currentUser = auth.currentUser;
        if (currentUser) {
            try {
                await currentUser.reload();
                await updateProfile(currentUser, { photoURL: avatarURL });
                setUser(prevUser => ({ ...prevUser, photoURL: avatarURL }));
            } catch (error) {
                console.error('Chyba při aktualizaci avataru:', error);
            }
        } else {
            console.error('Chyba: Žádný uživatel není přihlášen.');
        }
    };

    const updateUserDisplayName = async (displayName) => {
        const currentUser = auth.currentUser;
        if (currentUser) {
            try {
                await currentUser.reload();
                await updateProfile(currentUser, { displayName });
                setUser(prevUser => ({ ...prevUser, displayName }));
            } catch (error) {
                console.error('Chyba při aktualizaci zobrazeného jména:', error);
            }
        } else {
            console.error('Chyba: Žádný uživatel není přihlášen.');
        }
    };

    const reauthenticateUser = async (password) => {
        const currentUser = auth.currentUser;
        if (currentUser) {
            try {
                const credential = EmailAuthProvider.credential(currentUser.email, password);
                await reauthenticateWithCredential(currentUser, credential);
                console.log('Reautentizace úspěšná');
            } catch (error) {
                console.error('Chyba při reautentizaci:', error);
                throw error;
            }
        } else {
            console.error('Chyba: Žádný uživatel není přihlášen.');
        }
    };

    const deleteUser = async () => {
        const currentUser = auth.currentUser;
        if (currentUser) {
            try {
                // Smazání dokumentu uživatele z Firestore
                await deleteUserAccount(currentUser.uid);

                // Smazání uživatele z Firebase Authentication
                await firebaseDeleteUser(currentUser);
                setUser(null);
                setRole(null);
                console.log('Uživatelský účet smazán');
            } catch (error) {
                console.error('Chyba při mazání uživatelského účtu:', error);
                throw error;
            }
        } else {
            console.error('Chyba: Žádný uživatel není přihlášen.');
        }
    };

    const getUserEmail = () => {
        return user?.email || '';
    };

    const getUserAvatar = () => {
    };

    return (
        <AuthContext.Provider value={{ 
            user, 
            role, 
            login, 
            logout, 
            updateAvatar, 
            updateUserDisplayName, 
            reauthenticateUser, 
            deleteUser, 
            getUserEmail, 
            getUserAvatar,
            loading,
            watchlist: watchlistState.items,
            watchlistMap: watchlistState.map,
            notifications,
            notifSeenAt,
            markNotificationsSeen,
            createNotification,
            deleteNotification,
            followedMap,
            toggleFollow
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
